import type { TooltipProps } from 'antd';
import classNames from 'classnames';
import React from 'react';

import Tooltip from 'ui/base/Tooltip';

type EllipsisProps = {
  clamp?: number;
  className?: string;
  tooltipProps?: Omit<TooltipProps, 'title'> & { title?: React.ReactNode };
  component?: React.ElementType;
  tooltipCustomContent?: ((isClamped: boolean) => React.ReactNode);
} & React.PropsWithChildren;

const Ellipsis: React.FC<EllipsisProps> = ({ clamp, tooltipProps, component: Component = 'div', children, tooltipCustomContent, className: classNameProp }) => {
  const ref = React.useRef<HTMLElement>(undefined);
  const [isClamped, setIsClamped] = React.useState(false);

  const isEllipsis = typeof clamp === 'undefined';
  const className = classNames({
    'ellipsis overflow-hidden text-nowrap': isEllipsis,
    [`line-clamp line-clamp-${clamp}`]: !isEllipsis
  }, classNameProp);

  const checkEllipsis = React.useCallback(() => {
    if (ref?.current) {
      const { scrollHeight, clientHeight, scrollWidth, clientWidth } = ref.current;
      if (isEllipsis) {
        setIsClamped(scrollWidth > clientWidth);
      } else {
        const lineHeight = parseInt(window.getComputedStyle(ref.current).lineHeight, 10);
        const lines = Math.floor(clientHeight / lineHeight);
        setIsClamped(scrollHeight > lines * lineHeight);
      }
    }
  }, [isEllipsis]);

  React.useLayoutEffect(() => {
    const node = ref?.current;
    checkEllipsis();
    const resizeObserver = new ResizeObserver(checkEllipsis);
    if (node) {
      resizeObserver.observe(node);
    }

    return () => {
      if (node) {
        resizeObserver.unobserve(node);
      }
    };
  }, [ref, checkEllipsis]);

  const defaultTooltipContent = isClamped ? children : null;
  const resolvedTooltipContent = tooltipCustomContent ? tooltipCustomContent(isClamped) : defaultTooltipContent;

  return resolvedTooltipContent ? (
    <Tooltip title={resolvedTooltipContent} placement="top" {...tooltipProps}>
      <Component ref={ref} className={classNames('enabled', className)}>
        {children}
      </Component>
    </Tooltip>
  ) : (
    <Component ref={ref} className={className}>
      {children}
    </Component>
  );
};

export default Ellipsis;
